import debounce from "lodash.debounce";
import type { ChangeEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { css, styled } from "styled-components";

import { ForwardIcon, PauseIcon, PlayIcon, ReplayIcon, Spinner, UIButton } from "common/components";
import { useTranslate } from "common/utilities/useTranslate";
import type { AudioRef, OnProgressProps, OnTimeUpdateProps } from "domains/audioPlayer/components/Audio";
import { Audio } from "domains/audioPlayer/components/Audio";
import { AudioTrack } from "domains/audioPlayer/components/AudioTrack";
import type { AudioFile } from "domains/audioPlayer/types";

const StyledContent = styled.div`
  color: ${({ theme }) => theme.colors.textFade};
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  flex: 1;
`;

const StyledTitle = styled.div`
  font-size: 0.8rem;
  line-height: 1rem;
  text-transform: uppercase;
`;

const StyledName = styled.div`
  font-size: 0.8rem;
  line-height: 0.9rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.textBright};
`;

const StyledControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
`;

const iconStyles = css`
  width: 42px;
  height: 42px;
  cursor: pointer;
  transition: color 0.3s;
  color: ${({ theme }) => theme.colors.textFade};

  &:hover {
    color: ${({ theme }) => theme.colors.textBright};
  }
`;

const StyledPlayIcon = styled(PlayIcon)`
  ${iconStyles};
`;

const StyledPauseIcon = styled(PauseIcon)`
  ${iconStyles};
`;

const iconSmallStyles = css`
  width: 32px;
  height: 32px;
  cursor: pointer;
  transition: color 0.3s;
  color: ${({ theme }) => theme.colors.textFade};

  &:hover {
    color: ${({ theme }) => theme.colors.textBright};
  }
`;

const StyledReplayIcon = styled(ReplayIcon)`
  ${iconSmallStyles};
`;

const StyledForwardIcon = styled(ForwardIcon)`
  ${iconSmallStyles};
`;

const StyledSpinner = styled(Spinner)`
  ${iconStyles};
`;

const StyledButton = styled(UIButton)`
  background: none;
`;

type AudioPlayerProps = { audioFile: AudioFile };

export const AudioPlayer = ({ audioFile }: AudioPlayerProps) => {
  const translate = useTranslate();
  const audioRef = useRef<AudioRef>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [defaultTime, setDefaultTime] = useState(0);
  const [hasErrorOccurred, setHasErrorOccurred] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [buffered, setBufferedAmounts] = useState<TimeRanges | undefined>(undefined);
  const bufferedTime = buffered?.length ? buffered.end(buffered.length - 1) : 0;

  const togglePlaying = () => {
    setHasErrorOccurred(false);
    if (!isPlaying) {
      setIsLoading(true);
    }
    setIsPlaying(!isPlaying);
  };

  const onProgress = useCallback(({ trackDuration, buffered }: OnProgressProps) => {
    if (Number.isFinite(trackDuration)) {
      setDuration(Math.floor(trackDuration));
    }
    setBufferedAmounts(buffered);
  }, []);

  useEffect(() => {
    if (!isLoading) {
      return;
    }

    if (bufferedTime >= currentTime) {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buffered]);

  const onTimeUpdate = useCallback(
    ({ currentTime, trackDuration, buffered }: OnTimeUpdateProps) => {
      const isCurrentTimeValid = Number.isFinite(currentTime);
      const isTrackDurationValid = Number.isFinite(trackDuration);
      if (isCurrentTimeValid) {
        setCurrentTime(Math.floor(currentTime));
      }
      if (isTrackDurationValid) {
        onProgress({ trackDuration, buffered });
      }
      if (isCurrentTimeValid && isTrackDurationValid && currentTime > defaultTime) {
        setIsLoading(false);
      }
    },
    [defaultTime, onProgress],
  );

  const debouncedSetDefaultTime = debounce((value: number) => {
    if (value > currentTime) {
      setIsLoading(true);
    }
    setDefaultTime(value);
  }, 100);

  const onTrackChange = (event: ChangeEvent<HTMLInputElement>) => {
    const time = parseInt(event.target.value);
    setCurrentTime(time);
    debouncedSetDefaultTime(time);
  };

  const onError = useCallback(() => {
    setHasErrorOccurred(true);
    setIsPlaying(false);
    setIsLoading(false);
    setDefaultTime(0);
  }, []);

  const onEnd = useCallback(() => setIsPlaying(false), []);

  const onSetTime = (seconds: number) => () => {
    audioRef?.current?.updateTimeBySeconds(seconds);
  };

  useEffect(() => {
    setIsLoading(true);
  }, [audioFile.url]);

  if (hasErrorOccurred) {
    return null;
  }

  return (
    <>
      <StyledContent>
        <StyledTitle>{translate("app.topics.podcast.overview_of")}</StyledTitle>
        <StyledName>{audioFile.name}</StyledName>
        <StyledControls>
          <StyledButton aria-label="Rewind 10 seconds" onClick={onSetTime(-10)} variant="neutral">
            <StyledReplayIcon />
          </StyledButton>
          <StyledButton aria-label={isPlaying ? "Pause" : "Play"} onClick={togglePlaying} variant="neutral">
            {isLoading ? <StyledSpinner /> : isPlaying ? <StyledPauseIcon /> : <StyledPlayIcon />}
          </StyledButton>
          <StyledButton aria-label="Skip forwards 10 seconds" onClick={onSetTime(10)} variant="neutral">
            <StyledForwardIcon />
          </StyledButton>
        </StyledControls>
        <AudioTrack
          currentTime={currentTime}
          duration={duration}
          onChange={onTrackChange}
          bufferedTime={bufferedTime}
        />
      </StyledContent>
      <Audio
        defaultTime={defaultTime}
        hidden
        isPlaying={isPlaying}
        onEnd={onEnd}
        onError={onError}
        onProgress={onProgress}
        onTimeUpdate={onTimeUpdate}
        ref={audioRef}
        source={audioFile.url}
      />
    </>
  );
};
